import { ChangeEvent, useState } from "react";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import {
  ReviewSaleDepositEditRequestRequest,
  SaleDepositEditRequest,
} from "../../types/sale-deposit-edit-request.type";
import { formatBusinessDate } from "../SaleDepositReport/saleCenterClosing.utils";

/** Bounded so an over-long note fails in the field rather than as a 400 from the server. */
const REVIEW_NOTE_MAX_LENGTH = 255;

export type EditRequestDecision = ReviewSaleDepositEditRequestRequest["status"];

interface DecisionDialogProps {
  request: SaleDepositEditRequest;
  decision: EditRequestDecision;
  saving: boolean;
  onClose: () => void;
  /** The note is optional on both decisions; `null` when nothing was typed. */
  onConfirm: (reviewNote: string | null) => void;
}

/**
 * The supervisor answering one request.
 *
 * Approving is confirmed rather than fired straight from the row, because it is not a tick
 * on a list: it unfreezes money that was already signed off, wipes this route's ตรวจแล้ว
 * ticks and reopens the whole branch-day, which somebody then has to close again. The dialog
 * says all of that in the words the rest of the screen uses, so the consequence is read
 * before the click rather than discovered after it.
 *
 * Refusing carries a note instead of a warning: a rejection is rarely the end — the branch
 * may raise another request — so the note is where the approver says what would make the
 * next one answerable.
 *
 * Mounted only while a row is being answered, which is what gives each decision a fresh note
 * draft and lets the request be required rather than nullable.
 */
export default function DecisionDialog({
  request,
  decision,
  saving,
  onClose,
  onConfirm,
}: DecisionDialogProps) {
  const [reviewNote, setReviewNote] = useState<string>("");

  const handleChangeNote = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setReviewNote(event.target.value);
  };

  const handleConfirm = () => {
    onConfirm(reviewNote.trim() || null);
  };

  const isApproval = decision === "approved";

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle sx={{ fontWeight: 700 }}>
        {isApproval ? "อนุมัติคำขอแก้ไขยอดโอน?" : "ปฏิเสธคำขอแก้ไขยอดโอน?"}
      </DialogTitle>
      <DialogContent dividers>
        <Stack gap={2}>
          {isApproval ? (
            <Alert severity="warning">
              เมื่ออนุมัติ ระบบจะเปิดให้สาขาแก้ไขรายการนี้ได้อีกครั้ง
              ล้างสถานะตรวจแล้วของรายการนี้ (สายส่งอื่นในวันเดียวกันไม่ถูกล้าง)
              และเปิดศูนย์เซลของวันนั้นอีกครั้ง
              จึงต้องปิดศูนย์เซลใหม่หลังตรวจสอบการแก้ไขเรียบร้อยแล้ว
            </Alert>
          ) : (
            <Alert severity="info">
              สาขาจะยังไม่สามารถแก้ไขรายการนี้ได้
              และสามารถส่งคำขอใหม่ได้หากระบุเหตุผลชัดเจนขึ้น
            </Alert>
          )}
          <Stack gap={0.5}>
            <Typography variant="body2" fontWeight={600}>
              สายส่ง {request.route_code} · สาขา {request.branch_code || "-"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              วันที่ทำรายการ {formatBusinessDate(request.business_date)} ·
              ผู้ขอ {request.requested_by_name || "-"}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              เหตุผลที่ขอแก้ไข: {request.reason}
            </Typography>
          </Stack>
          {!isApproval && (
            <TextField
              label="เหตุผลที่ปฏิเสธ (ไม่บังคับ)"
              placeholder="ระบุสิ่งที่สาขาต้องชี้แจงเพิ่ม"
              value={reviewNote}
              onChange={handleChangeNote}
              fullWidth
              multiline
              rows={2}
              size="small"
              slotProps={{ htmlInput: { maxLength: REVIEW_NOTE_MAX_LENGTH } }}
              helperText={`${reviewNote.length}/${REVIEW_NOTE_MAX_LENGTH}`}
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} color="inherit">
          ยกเลิก
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color={isApproval ? "success" : "error"}
          loading={saving}
        >
          {isApproval ? "ยืนยันอนุมัติ" : "ยืนยันปฏิเสธ"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
